/**
 * Safely parses localStorage values to handle non-standard browser behaviors
 * Specifically handles cases where browsers (like Apple Mail's embedded browser)
 * return the string "undefined" instead of null for missing keys
 */
export const safeParseLocalStorage = (key: string, fallback: any = null) => {
  try {
    // Check if window and localStorage are available (SSR-safe)
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return fallback;
    }

    const value = localStorage?.getItem(key);

    // Handle null, undefined, or empty string
    if (!value || value === 'undefined' || value === 'null') {
      return fallback;
    }

    return JSON.parse(value);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`[StartEngine] Failed to parse localStorage value for key "${key}":`, error);
    return fallback;
  }
};

/**
 * Safely gets a localStorage value without parsing
 * Returns null for missing keys or non-standard "undefined" strings
 */
export const safeGetLocalStorage = (key: string): string | null => {
  try {
    // Check if window and localStorage are available (SSR-safe)
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return null;
    }

    const value = localStorage?.getItem(key);

    // Handle non-standard "undefined" string return from some browsers
    if (value === 'undefined' || value === 'null') {
      return null;
    }

    return value;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`[StartEngine] Failed to get localStorage value for key "${key}":`, error);
    return null;
  }
};
